import { useEffect, useState } from "react";
import { ArrowRight, Calendar, Clock, ExternalLink, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import serviceInvest from "@/assets/service-invest.jpg";
import aboutCaribbean from "@/assets/about-caribbean.jpg";
import { supabase } from "@/integrations/supabase/client";
import { eventDateTimestamp, formatEventDate, isEventPast } from "@/lib/eventDates";
import { HELOC_EVENT, HELOC_EVENT_SLUG } from "@/data/events";

interface EventCard {
  title: string;
  date: string;
  dateTime: string;
  timeRange: string;
  locationName: string;
  locationLine1: string;
  locationLine2: string;
  format: string;
  description: string;
  category: string;
  image: string;
  href: string;
  slug?: string;
}

const FALLBACK_EVENTS: EventCard[] = [
  {
    title: "Sell Paradise, Earn Commission: Dominican Republic Listings",
    date: "2026-05-07",
    dateTime: "Thursday, May 7",
    timeRange: "6:00 PM - 8:00 PM",
    locationName: "Inc.ubate Coworking Lynn",
    locationLine1: "614 Washington Street, 2nd Floor",
    locationLine2: "Lynn, MA 01901",
    format: "Free · In person · 2 hours",
    description:
      "Ready to boost your sales and tap into the Dominican Republic market? Join us in person to discover top listings, learn how to earn commission, and connect with agents interested in selling paradise.",
    category: "For Agents",
    image: serviceInvest,
    href: "https://www.eventbrite.com/e/sell-paradise-earn-commission-dominican-republic-listings-tickets-1986952155384?aff=oddtdtcreator&keep_tld=true",
  },
  {
    title: "Buy in Paradise, Earn Income: Dominican Republic Real Estate",
    date: "2026-05-09",
    dateTime: "Saturday, May 9",
    timeRange: "2:00 PM - 4:00 PM",
    locationName: "North Shore Latino Business Association",
    locationLine1: "33 Sutton Street",
    locationLine2: "Lynn, MA 01901",
    format: "Free · In person · 2 hours",
    description:
      "Join us in person to learn how to buy property in the Dominican Republic and start earning income from your investment. Whether you are a first-time buyer or seasoned investor, this event will help you understand the opportunity and next steps.",
    category: "For Buyers & Investors",
    image: aboutCaribbean,
    href: "https://www.eventbrite.com/e/buy-in-paradise-earn-income-dominican-republic-real-estate-tickets-1986844374007?aff=oddtdtcreator&keep_tld=true",
  },
];

const FALLBACK_IMAGES = [serviceInvest, aboutCaribbean];

const HELOC_EVENT_CARD: EventCard = {
  title: HELOC_EVENT.title,
  date: HELOC_EVENT.event_date,
  dateTime: HELOC_EVENT.date_time_label || "",
  timeRange: HELOC_EVENT.time_range || "",
  locationName: HELOC_EVENT.location_name || "",
  locationLine1: HELOC_EVENT.location_line_1 || "",
  locationLine2: HELOC_EVENT.location_line_2 || "",
  format: HELOC_EVENT.format || "",
  description: HELOC_EVENT.description || "",
  category: HELOC_EVENT.category || "",
  image: HELOC_EVENT.image_url || serviceInvest,
  href: HELOC_EVENT.registration_url || "#",
  slug: HELOC_EVENT_SLUG,
};

const sortEvents = (list: EventCard[]) =>
  [...list].sort((a, b) => {
    const aPast = isEventPast(a.date);
    const bPast = isEventPast(b.date);
    if (aPast !== bPast) return aPast ? 1 : -1;
    return eventDateTimestamp(a.date) - eventDateTimestamp(b.date);
  });

const Events = () => {
  const [sortedEvents, setSortedEvents] = useState<EventCard[]>(() =>
    sortEvents([HELOC_EVENT_CARD, ...FALLBACK_EVENTS])
  );

  useEffect(() => {
    document.title = "Events | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute(
      "content",
      "Explore Hadassah Homes events for real estate buyers, sellers, agents, and Dominican Republic investors."
    );
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) canonical.href = "https://hadassahhomesinc.com/events";
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_published", true)
        .order("event_date", { ascending: true });
      if (!mounted) return;
      if (error || !data || data.length === 0) return; // keep static + legacy fallback
      const mapped: EventCard[] = data.map((e, idx) => ({
        title: e.title,
        date: e.event_date,
        dateTime:
          e.date_time_label ||
          formatEventDate(e.event_date, undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
          }),
        timeRange: e.time_range || "",
        locationName: e.location_name || "",
        locationLine1: e.location_line_1 || "",
        locationLine2: e.location_line_2 || "",
        format: e.format || "",
        description: e.description || "",
        category: e.category || "",
        image: e.image_url || FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length],
        href: e.registration_url || "#",
        slug: e.slug || undefined,
      }));
      setSortedEvents(
        sortEvents([
          HELOC_EVENT_CARD,
          ...mapped.filter((event) => event.slug !== HELOC_EVENT_SLUG),
        ])
      );
    })();
    return () => {
      mounted = false;
    };
  }, []);


  return (
    <SiteLayout>
      <PageHero
        eyebrow="Events"
        title="Hadassah Homes Events"
        subtitle="Educational events for buyers, sellers, agents, and investors exploring real estate opportunities in Massachusetts and the Dominican Republic."
      />

      <ContentSection>
        <div className="space-y-8 md:space-y-10">
          <div className="mx-auto max-w-3xl text-center space-y-4">
            <p className="text-muted-foreground text-base leading-relaxed md:text-lg">
              Join upcoming Hadassah Homes events designed to help you understand market opportunity, strategy, and the next step that fits your goals.
            </p>
            <p className="text-muted-foreground text-sm md:text-base">
              Stay up to date on all events —{" "}
              <a
                href="https://www.eventbrite.com/o/121220468768"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent/80 transition-colors underline underline-offset-2"
              >
                follow us on Eventbrite
              </a>
              .
            </p>
          </div>

          {sortedEvents.filter(e => !isEventPast(e.date)).length === 0 && (
            <p className="text-center text-muted-foreground text-base md:text-lg py-8">
              No upcoming events right now — check back soon.
            </p>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            {sortedEvents.map((event) => {
              const past = isEventPast(event.date);
              return (
                <article
                  key={event.title}
                  className={`flex h-full flex-col overflow-hidden rounded-none border bg-white shadow-[0_24px_70px_rgba(0,0,0,0.10)] transition-all duration-300 ${
                    past
                      ? "border-border/40 opacity-70"
                      : "border-[#C9A95C]/25 hover:-translate-y-1 hover:shadow-[0_30px_80px_rgba(0,0,0,0.14)]"
                  }`}
                >
                  <div className="relative h-64 overflow-hidden rounded-none md:h-72">
                    <img
                      src={event.image}
                      alt={event.title}
                      className={`h-full w-full object-cover ${past ? "grayscale" : ""}`}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                    {past && (
                      <div className="absolute top-4 left-4 bg-black/70 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.18em] text-white/80">
                        Past Event
                      </div>
                    )}
                  </div>
                  <div className={`h-px w-full ${past ? "bg-border/40" : "bg-[#C9A95C]/35"}`} />

                  <div className="flex flex-1 flex-col space-y-5 p-7 md:p-8">
                    <div className="space-y-5">
                      <span className={`inline-flex w-fit font-body text-xs font-semibold uppercase tracking-[0.18em] ${past ? "text-muted-foreground" : "text-[#B89A50]"}`}>
                        {past ? "Past Event" : event.category}
                      </span>
                      <h2 className="font-headline text-2xl leading-tight text-[#111111] md:text-3xl">
                        {event.title}
                      </h2>
                      <div className="space-y-3 border-y border-[#C9A95C]/20 py-5">
                        <div className="flex items-start gap-3">
                          <Calendar className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[#B89A50]" />
                          <p className="text-sm leading-relaxed text-[#333333] md:text-base">{event.dateTime}</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <Clock className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[#B89A50]" />
                          <p className="text-sm leading-relaxed text-[#333333] md:text-base">{event.timeRange}</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[#B89A50]" />
                          <div className="space-y-1">
                            <p className="text-sm leading-relaxed text-[#333333] md:text-base">{event.locationName}</p>
                            <p className="text-sm leading-relaxed text-[#333333] md:text-base">{event.locationLine1}</p>
                            <p className="text-sm leading-relaxed text-[#333333] md:text-base">{event.locationLine2}</p>
                          </div>
                        </div>
                      </div>
                      <p className="font-body text-xs font-semibold uppercase tracking-[0.16em] text-[#B89A50]">
                        {event.format}
                      </p>
                      <p className="text-base leading-relaxed text-[#444444]">
                        {event.description}
                      </p>
                    </div>

                    <div className="mt-auto pt-2">
                      {past ? (
                        <span className="inline-flex w-full items-center justify-center gap-2 rounded-none border border-border/50 px-6 py-4 font-body text-sm uppercase tracking-[0.12em] text-muted-foreground sm:w-auto cursor-default">
                          This Event Has Passed
                        </span>
                      ) : event.slug ? (
                        <Link
                          to={`/events/${event.slug}`}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-none bg-[#173D2D] px-6 py-4 font-body text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#0F3326] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#173D2D]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:w-auto"
                        >
                          View Event Details
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      ) : (
                        <a
                          href={event.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex w-full items-center justify-center gap-2 rounded-none bg-[#173D2D] px-6 py-4 font-body text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#0F3326] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#173D2D]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:w-auto"
                        >
                          View Event
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <div className="mx-auto max-w-3xl text-center space-y-5">
          <p className="font-body text-xs uppercase tracking-[0.14em] text-accent">
            Next Step
          </p>
          <h2 className="font-headline text-3xl leading-tight text-secondary-foreground md:text-5xl">
            Have Questions Before Registering?
          </h2>
          <p className="text-base leading-relaxed text-secondary-foreground/82 md:text-lg">
            Book a consultation and we&apos;ll help you understand which opportunity fits your goals.
          </p>
          <div className="pt-2">
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 bg-accent px-8 py-4 font-body text-sm uppercase tracking-[0.12em] text-accent-foreground transition-colors hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
            >
              Book a Consultation
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </ContentSection>
    </SiteLayout>
  );
};

export default Events;
